import React from 'react';
import {view} from '@sail/ui';

export interface IOverlayContext {
  overlayHidden: boolean;
  container: HTMLElement | null;
  count: number;
  handleOverlayClose: () => void;
  handleOverlayOpen: (onNewIndex: (newIndex: number) => void) => void;
  hideOverlayDuringCallback<T>(callback: () => Promise<T>): Promise<T>;
}

// eslint-disable-next-line @stripe-internal/stripe/no-mixed-react-exports
export const OverlayContext = React.createContext<IOverlayContext | null>(null);
OverlayContext.displayName = 'OverlayContext';

// eslint-disable-next-line @stripe-internal/stripe/no-mixed-react-exports
export const useOverlayContainer = () => {
  const context = React.useContext(OverlayContext);
  if (!context) {
    throw Error('Overlay context is not defined');
  }
  return context.container;
};

// eslint-disable-next-line @stripe-internal/stripe/no-mixed-react-exports
export const useIsTopOverlay = () => {
  const context = React.useContext(OverlayContext);
  if (!context) {
    throw Error('Overlay context is not defined');
  }
  const {count, handleOverlayOpen, handleOverlayClose} = context;

  const [index, setIndex] = React.useState<number | undefined>();

  React.useEffect(() => {
    handleOverlayOpen(setIndex);
    return () => {
      handleOverlayClose();
    };
  }, [handleOverlayClose, handleOverlayOpen]);

  return typeof index !== 'undefined' && index === count;
};

// Do not use OverlayProvider directly. Instead, use ConnectEmbeddedLibWrapper
export const OverlayProviderDoNotUse: React.FC = ({children}) => {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [count, setCount] = React.useState<number>(0);
  const [overlayHidden, setOverlayHidden] = React.useState<boolean>(false);

  const handleOverlayClose = React.useCallback(() => {
    setCount((prev) => prev - 1);
  }, []);

  const handleOverlayOpen = React.useCallback((onNewCount) => {
    setCount((prev) => {
      const newCount = prev + 1;
      onNewCount(newCount);
      return newCount;
    });
  }, []);

  async function hideOverlayDuringCallback<T>(
    callback: () => Promise<T>,
  ): Promise<T> {
    setCount((prev) => prev + 1);
    setOverlayHidden(true);
    try {
      return await callback();
    } finally {
      setOverlayHidden(false);
      handleOverlayClose();
    }
  }

  return (
    <OverlayContext.Provider
      value={{
        overlayHidden,
        container: ref.current,
        count,
        handleOverlayClose,
        handleOverlayOpen,
        hideOverlayDuringCallback,
      }}
    >
      <view.div ref={ref}>{children}</view.div>
    </OverlayContext.Provider>
  );
};
